import React, { useState, useEffect, useRef } from 'react';
import * as d3 from 'd3';
import { Download, Play, Pause, ZoomIn, ZoomOut } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || '';

const NetworkGraph = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSimulationRunning, setIsSimulationRunning] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('ALL');
  const [selectedNode, setSelectedNode] = useState(null);
  const [showDetailPanel, setShowDetailPanel] = useState(false);
  const svgRef = useRef();
  const containerRef = useRef();
  const simulationRef = useRef();

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (data && svgRef.current) {
      drawNetwork();
    }
    return () => {
      if (simulationRef.current) {
        simulationRef.current.stop();
      }
    };
  }, [data, selectedCategory]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [productsRes, manufacturersRes] = await Promise.all([
        fetch(`${API_URL}/api/trm/products`),
        fetch(`${API_URL}/api/trm/manufacturers`)
      ]);
      
      if (!productsRes.ok || !manufacturersRes.ok) throw new Error('Failed to fetch data');
      
      const products = await productsRes.json();
      const manufacturers = await manufacturersRes.json();
      
      processData(products.products || [], manufacturers.manufacturers || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const processData = (products, manufacturers) => {
    // Filter by category if selected
    const filteredProducts = selectedCategory === 'ALL' 
      ? products 
      : products.filter(p => p.trm_category === selectedCategory);

    // Get unique manufacturer IDs from filtered products
    const usedManufacturerIds = new Set(filteredProducts.map(p => p.manufacturer_id));

    // Create nodes
    const nodes = [
      // Manufacturer nodes
      ...manufacturers
        .filter(m => usedManufacturerIds.has(m.manufacturer_id))
        .map(m => ({
          id: `mfr-${m.manufacturer_id}`,
          type: 'manufacturer',
          name: m.canonical_name,
          productCount: m.product_count,
          versionCount: m.version_count,
          manufacturerId: m.manufacturer_id
        })),
      // Product nodes
      ...filteredProducts.map(p => ({
        id: `prod-${p.product_id}`,
        type: 'product',
        name: p.canonical_name,
        category: p.trm_category,
        versionCount: p.version_count,
        status: p.record_status,
        manufacturerId: p.manufacturer_id
      }))
    ];

    // Create links
    const links = filteredProducts.map(p => ({
      source: `mfr-${p.manufacturer_id}`,
      target: `prod-${p.product_id}`,
      category: p.trm_category
    }));

    setData({ nodes, links });
  };

  const drawNetwork = () => {
    if (!data) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const container = containerRef.current;
    const width = container.clientWidth;
    const height = 800;

    svg.attr('width', width).attr('height', height);

    // Color scale for categories
    const colorScale = d3.scaleOrdinal()
      .domain(['Database', 'DevOps', 'Security', 'Collaboration', 'ITSM', 'Monitoring', 'Networking', 'Cloud Infrastructure', 'Analytics', 'Automation'])
      .range(d3.schemeTableau10);

    // Zoom behavior
    const zoom = d3.zoom()
      .scaleExtent([0.1, 4])
      .on('zoom', (event) => {
        g.attr('transform', event.transform);
      });

    svg.call(zoom);

    const g = svg.append('g');

    // Tooltip
    const tooltip = d3.select('body').append('div')
      .attr('class', 'network-tooltip')
      .style('position', 'absolute')
      .style('visibility', 'hidden')
      .style('background', 'rgba(0, 0, 0, 0.9)')
      .style('color', 'white')
      .style('padding', '12px')
      .style('border-radius', '8px')
      .style('font-size', '13px')
      .style('pointer-events', 'none')
      .style('z-index', '10000')
      .style('box-shadow', '0 4px 12px rgba(0,0,0,0.3)');

    // Force simulation
    const simulation = d3.forceSimulation(data.nodes)
      .force('link', d3.forceLink(data.links).id(d => d.id).distance(100))
      .force('charge', d3.forceManyBody().strength(-300))
      .force('center', d3.forceCenter(width / 2, height / 2))
      .force('collision', d3.forceCollide().radius(d => d.type === 'manufacturer' ? 35 : 25));

    simulationRef.current = simulation;

    // Draw links
    const link = g.append('g')
      .selectAll('line')
      .data(data.links)
      .enter()
      .append('line')
      .attr('stroke', d => colorScale(d.category))
      .attr('stroke-width', 2)
      .attr('stroke-opacity', 0.6);

    // Draw nodes
    const node = g.append('g')
      .selectAll('g')
      .data(data.nodes)
      .enter()
      .append('g')
      .call(d3.drag()
        .on('start', dragstarted)
        .on('drag', dragged)
        .on('end', dragended));

    // Node circles
    node.append('circle')
      .attr('r', d => d.type === 'manufacturer' ? 30 : 20)
      .attr('fill', d => d.type === 'manufacturer' ? '#3b82f6' : colorScale(d.category))
      .attr('stroke', '#fff')
      .attr('stroke-width', 3)
      .attr('opacity', 0.9)
      .style('cursor', 'pointer')
      .on('mouseover', function(event, d) {
        d3.select(this)
          .attr('stroke', '#fbbf24')
          .attr('stroke-width', 4)
          .attr('opacity', 1);

        // Highlight connected links
        link.attr('stroke-opacity', l => 
          (l.source.id === d.id || l.target.id === d.id) ? 1 : 0.1
        );

        if (d.type === 'manufacturer') {
          tooltip.style('visibility', 'visible')
            .html(`
              <div style="font-weight: 700; font-size: 16px; margin-bottom: 8px; color: var(--cares-blue);">
                🏢 ${d.name}
              </div>
              <div style="color: var(--cares-text-dim); margin-bottom: 4px;">
                ${d.productCount} product${d.productCount !== 1 ? 's' : ''}
              </div>
              <div style="color: var(--cares-text-note);">
                ${d.versionCount} versions
              </div>
            `);
        } else {
          tooltip.style('visibility', 'visible')
            .html(`
              <div style="font-weight: 700; font-size: 16px; margin-bottom: 8px;">
                📦 ${d.name}
              </div>
              <div style="color: ${colorScale(d.category)}; margin-bottom: 4px;">
                ${d.category}
              </div>
              <div style="color: var(--cares-text-dim); margin-bottom: 4px;">
                ${d.versionCount} versions
              </div>
              <div style="color: var(--cares-text-note);">
                Status: ${d.status}
              </div>
            `);
        }
      })
      .on('mousemove', function(event) {
        tooltip
          .style('top', (event.pageY - 10) + 'px')
          .style('left', (event.pageX + 15) + 'px');
      })
      .on('mouseout', function() {
        d3.select(this)
          .attr('stroke', '#fff')
          .attr('stroke-width', 3)
          .attr('opacity', 0.9);

        link.attr('stroke-opacity', 0.6);
        tooltip.style('visibility', 'hidden');
      })
      .on('click', function(event, d) {
        event.stopPropagation();
        setSelectedNode(d);
        setShowDetailPanel(true);
      });

    // Node labels
    node.append('text')
      .attr('text-anchor', 'middle')
      .attr('dy', d => d.type === 'manufacturer' ? 45 : 33)
      .attr('font-size', d => d.type === 'manufacturer' ? '11px' : '9px')
      .attr('font-weight', d => d.type === 'manufacturer' ? '700' : '600')
      .attr('fill', '#1f2937')
      .attr('pointer-events', 'none')
      .text(d => d.name.length > 12 ? d.name.substring(0, 12) + '...' : d.name);

    // Update positions on tick
    simulation.on('tick', () => {
      link
        .attr('x1', d => d.source.x)
        .attr('y1', d => d.source.y)
        .attr('x2', d => d.target.x)
        .attr('y2', d => d.target.y);

      node.attr('transform', d => `translate(${d.x},${d.y})`);
    });

    // Drag functions
    function dragstarted(event, d) {
      if (!event.active) simulation.alphaTarget(0.3).restart();
      d.fx = d.x;
      d.fy = d.y;
    }

    function dragged(event, d) {
      d.fx = event.x;
      d.fy = event.y;
    }

    function dragended(event, d) {
      if (!event.active) simulation.alphaTarget(0);
      d.fx = null;
      d.fy = null;
    }

    return () => {
      tooltip.remove();
    };
  };

  const toggleSimulation = () => {
    if (simulationRef.current) {
      if (isSimulationRunning) {
        simulationRef.current.stop();
      } else {
        simulationRef.current.alpha(0.3).restart();
      }
      setIsSimulationRunning(!isSimulationRunning);
    }
  };

  const zoomIn = () => {
    const svg = d3.select(svgRef.current);
    svg.transition().call(d3.zoom().scaleBy, 1.3);
  };

  const zoomOut = () => {
    const svg = d3.select(svgRef.current);
    svg.transition().call(d3.zoom().scaleBy, 0.7);
  };

  const exportSVG = () => {
    const svgElement = svgRef.current;
    const serializer = new XMLSerializer();
    const svgString = serializer.serializeToString(svgElement);
    const blob = new Blob([svgString], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'trm-network.svg';
    link.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <div style={{ padding: '40px', textAlign: 'center' }}>Loading network graph...</div>;
  if (error) return <div style={{ padding: '40px', color: 'var(--cares-orange)' }}>Error: {error}</div>;

  const categories = ['ALL', 'Database', 'DevOps', 'Security', 'Collaboration', 'ITSM', 'Monitoring', 'Networking', 'Cloud Infrastructure', 'Analytics', 'Automation'];

  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
          <div>
            <h1 style={{ fontSize: '28px', fontWeight: 'bold', color: 'var(--cares-text-2)', marginBottom: '8px' }}>
              TRM Network Graph
            </h1>
            <p style={{ fontSize: '14px', color: 'var(--cares-text-label)', marginBottom: '12px' }}>
              Interactive force-directed visualization of manufacturers and products
            </p>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={toggleSimulation} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', background: isSimulationRunning ? 'var(--cares-orange)' : 'var(--cares-green)', color: 'var(--cares-text-max)', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px' }}>
              {isSimulationRunning ? <><Pause size={14} /> Pause</> : <><Play size={14} /> Play</>}
            </button>
            <button onClick={zoomIn} style={{ padding: '8px 12px', background: 'var(--cares-surface-raised)', color: 'var(--cares-text-max)', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>
              <ZoomIn size={14} />
            </button>
            <button onClick={zoomOut} style={{ padding: '8px 12px', background: 'var(--cares-surface-raised)', color: 'var(--cares-text-max)', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>
              <ZoomOut size={14} />
            </button>
            <button onClick={exportSVG} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', background: 'var(--cares-blue)', color: 'var(--cares-text-max)', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px' }}>
              <Download size={14} /> Export
            </button>
          </div>
        </div>

        {/* Category filter */}
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              style={{
                padding: '8px 16px',
                background: selectedCategory === cat ? 'var(--cares-blue)' : 'var(--cares-surface-board)',
                color: selectedCategory === cat ? 'var(--cares-text-max)' : 'var(--cares-text-label)',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: selectedCategory === cat ? '600' : '400',
                transition: 'all 0.2s'
              }}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      {/* Visualization */}
      <div ref={containerRef} style={{ background: 'var(--cares-surface-card)', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
        <svg ref={svgRef}></svg>
      </div>

      {/* Legend */}
      <div style={{ marginTop: '16px', display: 'flex', gap: '24px', padding: '16px', background: 'var(--cares-surface-board)', borderRadius: '8px', fontSize: '13px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ width: '20px', height: '20px', borderRadius: '50%', background: 'var(--cares-blue)', border: '2px solid var(--cares-line-card)' }}></div>
          <span style={{ color: 'var(--cares-text-label)' }}>Manufacturer</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ width: '16px', height: '16px', borderRadius: '50%', background: 'var(--cares-green)', border: '2px solid var(--cares-line-card)' }}></div>
          <span style={{ color: 'var(--cares-text-label)' }}>Product (color = category)</span>
        </div>
      </div>

      {/* Instructions */}
      <div style={{ marginTop: '16px', padding: '16px', background: 'color-mix(in srgb, var(--cares-blue) 14%, var(--cares-surface-card))', borderRadius: '8px', fontSize: '13px', color: 'var(--cares-blue)' }}>
        <strong>💡 Tip:</strong> Drag nodes to reposition • Scroll to zoom • Hover for details • <strong>Click nodes</strong> for detailed view • Filter by category
      </div>

      {/* Detail Panel */}
      {showDetailPanel && selectedNode && (
        <div style={{
          position: 'fixed',
          top: 0,
          right: 0,
          width: '400px',
          height: '100vh',
          background: 'var(--cares-surface-card)',
          boxShadow: '-4px 0 12px rgba(0,0,0,0.15)',
          zIndex: 1000,
          overflowY: 'auto',
          padding: '24px',
          animation: 'slideInRight 0.3s ease-out'
        }}>
          {/* Close button */}
          <button
            onClick={() => setShowDetailPanel(false)}
            style={{
              position: 'absolute',
              top: '16px',
              right: '16px',
              background: 'var(--cares-orange)',
              color: 'var(--cares-text-max)',
              border: 'none',
              borderRadius: '50%',
              width: '32px',
              height: '32px',
              cursor: 'pointer',
              fontSize: '18px',
              fontWeight: 'bold'
            }}
          >
            ×
          </button>

          {/* Detail content */}
          {selectedNode.type === 'manufacturer' ? (
            <>
              <div style={{ marginBottom: '24px' }}>
                <div style={{ fontSize: '12px', color: 'var(--cares-text-label)', marginBottom: '8px', textTransform: 'uppercase', fontWeight: '600' }}>
                  Manufacturer
                </div>
                <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: 'var(--cares-text-2)', marginBottom: '16px' }}>
                  {selectedNode.name}
                </h2>
              </div>

              {/* Stats */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
                <div style={{ padding: '16px', background: 'color-mix(in srgb, var(--cares-blue) 14%, var(--cares-surface-card))', borderRadius: '8px' }}>
                  <div style={{ fontSize: '12px', color: 'var(--cares-text-label)', marginBottom: '4px' }}>Products</div>
                  <div style={{ fontSize: '28px', fontWeight: 'bold', color: 'var(--cares-blue)' }}>
                    {selectedNode.productCount}
                  </div>
                </div>
                <div style={{ padding: '16px', background: 'color-mix(in srgb, var(--cares-green) 14%, var(--cares-surface-card))', borderRadius: '8px' }}>
                  <div style={{ fontSize: '12px', color: 'var(--cares-text-label)', marginBottom: '4px' }}>Versions</div>
                  <div style={{ fontSize: '28px', fontWeight: 'bold', color: 'var(--cares-green)' }}>
                    {selectedNode.versionCount}
                  </div>
                </div>
              </div>

              {/* Connected Products */}
              <div style={{ marginTop: '24px' }}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', color: 'var(--cares-text-2)', marginBottom: '16px' }}>
                  Connected Products
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {data.nodes
                    .filter(n => n.type === 'product' && n.manufacturerId === selectedNode.manufacturerId)
                    .map(product => (
                      <div
                        key={product.id}
                        style={{
                          padding: '12px',
                          background: 'var(--cares-surface-board)',
                          borderRadius: '8px',
                          borderLeft: `4px solid ${d3.scaleOrdinal(d3.schemeTableau10)(product.category)}`
                        }}
                      >
                        <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--cares-text-2)' }}>
                          {product.name}
                        </div>
                        <div style={{ fontSize: '12px', color: 'var(--cares-text-label)', marginTop: '4px' }}>
                          {product.category} • {product.versionCount} versions • {product.status}
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </>
          ) : (
            <>
              <div style={{ marginBottom: '24px' }}>
                <div style={{ fontSize: '12px', color: 'var(--cares-text-label)', marginBottom: '8px', textTransform: 'uppercase', fontWeight: '600' }}>
                  Product
                </div>
                <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: 'var(--cares-text-2)', marginBottom: '8px' }}>
                  {selectedNode.name}
                </h2>
                <div style={{
                  display: 'inline-block',
                  padding: '4px 12px',
                  background: `${d3.scaleOrdinal(d3.schemeTableau10)(selectedNode.category)}22`,
                  color: d3.scaleOrdinal(d3.schemeTableau10)(selectedNode.category),
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: '600'
                }}>
                  {selectedNode.category}
                </div>
              </div>

              {/* Stats */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '16px', marginBottom: '24px' }}>
                <div style={{ padding: '16px', background: 'color-mix(in srgb, var(--cares-blue) 14%, var(--cares-surface-card))', borderRadius: '8px' }}>
                  <div style={{ fontSize: '12px', color: 'var(--cares-text-label)', marginBottom: '4px' }}>Version Count</div>
                  <div style={{ fontSize: '28px', fontWeight: 'bold', color: 'var(--cares-blue)' }}>
                    {selectedNode.versionCount}
                  </div>
                </div>
                <div style={{ padding: '16px', background: selectedNode.status === 'Active' ? 'color-mix(in srgb, var(--cares-green) 14%, var(--cares-surface-card))' : 'color-mix(in srgb, var(--cares-amber) 22%, var(--cares-surface-card))', borderRadius: '8px' }}>
                  <div style={{ fontSize: '12px', color: 'var(--cares-text-label)', marginBottom: '4px' }}>Status</div>
                  <div style={{ fontSize: '20px', fontWeight: 'bold', color: selectedNode.status === 'Active' ? 'var(--cares-green)' : 'var(--cares-amber)' }}>
                    {selectedNode.status}
                  </div>
                </div>
              </div>

              {/* Manufacturer Info */}
              <div style={{ marginTop: '24px', padding: '16px', background: 'var(--cares-surface-board)', borderRadius: '8px' }}>
                <div style={{ fontSize: '12px', color: 'var(--cares-text-label)', marginBottom: '8px' }}>Manufacturer</div>
                <div style={{ fontSize: '16px', fontWeight: '600', color: 'var(--cares-text-2)' }}>
                  {data.nodes.find(n => n.type === 'manufacturer' && n.manufacturerId === selectedNode.manufacturerId)?.name || 'Unknown'}
                </div>
              </div>

              {/* Additional Info */}
              <div style={{ marginTop: '24px', padding: '16px', background: 'color-mix(in srgb, var(--cares-amber) 22%, var(--cares-surface-card))', borderRadius: '8px', borderLeft: '4px solid var(--cares-amber)' }}>
                <div style={{ fontSize: '13px', color: 'var(--cares-amber)' }}>
                  <strong>💡 Insight:</strong> This product has {selectedNode.versionCount} tracked versions
                  {selectedNode.versionCount > 20 && ', indicating high complexity and potential consolidation opportunity.'}
                  {selectedNode.versionCount <= 10 && ', showing good version control.'}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Overlay */}
      {showDetailPanel && (
        <div
          onClick={() => setShowDetailPanel(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.3)',
            zIndex: 999
          }}
        />
      )}
    </div>
  );
};

export default NetworkGraph;
